import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FaMapMarkerAlt } from 'react-icons/fa';
import { IoIosSearch } from 'react-icons/io';

import { AddressValue, CountryValue, useGetSourcesAddressesFilterQuery } from '@client/api';
import {
  Autocomplete,
  Button,
  combineStrings,
  EmptyPlaceholder,
  getCountries,
  GridCol,
  GridRow,
  HelperText,
  Input,
  isDefinedString,
  Label,
  Skeleton,
  Spinner,
  Typography,
  useSearching,
} from '@client/toolkit';

import { useTenantSettingsDefaults } from '../../../../../hooks';

const COUNTRIES = getCountries().map((x) => ({
  threeLetterCode: x.alpha3,
  twoLetterCode: x.alpha2,
  name: x.name,
}));

const NORWAY = COUNTRIES.find((x) => x.threeLetterCode === 'NOR') ?? COUNTRIES[0];

const matchesSearch = (value?: string | null, search?: string | null) =>
  !!value?.toLowerCase().includes(search?.toLowerCase() ?? '');

export interface BuildingAddressProps {
  defaultValue?: AddressValue;
  onChange: (address?: AddressValue) => void;
  editing: boolean;
  onToggleEdit: () => void;
  isEditMode?: boolean; // true when editing existing building, false when creating new
}

export const BuildingAddressForm = ({ defaultValue, onChange, editing, onToggleEdit, isEditMode = false }: BuildingAddressProps) => {
  const { defaultCountry } = useTenantSettingsDefaults();

  const { t, ready } = useTranslation('rentalObjects');
  const { t: tGlobal } = useTranslation('global');
  const { t: tCommon } = useTranslation();

  const {
    formState: { isSubmitting },
  } = useFormContext();

  // State for street name search (step 1)
  const {
    debouncedSearch: streetNameSearch,
    updateSearch: updateStreetNameSearch,
    searchDebouncing: streetNameSearchDebouncing,
  } = useSearching({ search: null, ms: 300 });

  // State for country search (for manual entry)
  const {
    search: countrySearch,
    updateSearch: updateCountrySearch,
  } = useSearching({ search: defaultValue?.country?.name ?? null });

  const mapCountryToAddress = useCallback(
    (country: CountryValue) => ({ country, zipCode: '', zipPlace: '', streetName: '', streetNumber: '' }),
    [],
  );

  const defaultAddress = useMemo(
    () => mapCountryToAddress(defaultCountry ?? NORWAY),
    [mapCountryToAddress, defaultCountry],
  );

  const [address, setAddress] = useState<AddressValue>(defaultValue ?? defaultAddress);
  const [streetSelected, setStreetSelected] = useState(!!defaultValue);
  const [numberSelected, setNumberSelected] = useState(!!defaultValue);
  const [manualEntry, setManualEntry] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  
  // Refs
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Auto-focus the search input when editing starts (for create mode)
  useEffect(() => {
    if (editing && !isEditMode) {
      // Use requestAnimationFrame to ensure DOM is ready, then focus after modal animation
      const focusInput = () => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      };
      
      // Focus after modal animation completes (typically 200-300ms)
      const timeoutId = setTimeout(focusInput, 300);
      
      return () => clearTimeout(timeoutId);
    }
  }, [editing, isEditMode]);

  // Search for street names using the filter API. The backend routes Norway to Norkart
  // and every other country to Nominatim (OpenStreetMap), so the selected country drives
  // which address source is queried.
  const { data: streetResults = [], isFetching: isFetchingStreets } = useGetSourcesAddressesFilterQuery(
    {
      countryThreeLetterCode: address.country?.threeLetterCode ?? NORWAY.threeLetterCode,
      streetName: streetNameSearch ?? undefined,
    },
    { skip: !streetNameSearch || streetNameSearch.length < 2 },
  );

  // Get all addresses with street numbers (no deduplication - show full addresses)
  const addressResults = useMemo(() => {
    return streetResults
      .filter((x) => x.streetName && x.streetNumber) // Only show addresses with street numbers
      .sort((a, b) => {
        // Sort by street name, then by street number
        const nameCompare = (a.streetName ?? '').localeCompare(b.streetName ?? '');
        if (nameCompare !== 0) return nameCompare;
        const numA = parseInt(a.streetNumber ?? '0', 10);
        const numB = parseInt(b.streetNumber ?? '0', 10);
        return numA - numB;
      });
  }, [streetResults]);

  // Handle search input change
  const handleSearchInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    updateStreetNameSearch(value);
    
    if (value.trim().length >= 2) {
      setShowDropdown(true);
    } else {
      setShowDropdown(false);
    }
    
    // Reset selection if user modifies input
    if (streetSelected) {
      setStreetSelected(false);
      setNumberSelected(false);
    }
  }, [updateStreetNameSearch, streetSelected]);

  // Get formatted address label for search results (defined early for use in handlers)
  const getAddressLabel = useCallback((x?: AddressValue) => {
    if (!x) return '';
    return `${x.streetName ?? ''}${x.streetNumber ? ` ${x.streetNumber}` : ''}, ${x.zipCode ?? ''} ${x.zipPlace ?? ''}`;
  }, []);

  // Handle address selection from dropdown (single step - includes street number)
  const handleSelectAddressFromDropdown = useCallback(
    (selected: AddressValue) => {
      const addressWithCountry = {
        ...selected,
        country: selected.country ?? NORWAY,
      };
      
      setSearchQuery(getAddressLabel(selected));
      setAddress(addressWithCountry);
      setStreetSelected(true);
      setNumberSelected(true);
      setShowDropdown(false);
      setHighlightedIndex(-1);
      updateCountrySearch(selected.country?.name ?? null);
      
      // Auto-apply: call onChange and toggle edit mode
      onChange(addressWithCountry);
      onToggleEdit();
    },
    [updateCountrySearch, getAddressLabel, onChange, onToggleEdit],
  );

  // Handle keyboard navigation
  const handleSearchKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (showDropdown && addressResults.length > 0) {
        setHighlightedIndex((prev) => Math.min(prev + 1, addressResults.length - 1));
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (showDropdown && addressResults.length > 0) {
        setHighlightedIndex((prev) => Math.max(prev - 1, 0));
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (showDropdown && highlightedIndex >= 0 && addressResults[highlightedIndex]) {
        handleSelectAddressFromDropdown(addressResults[highlightedIndex]);
      }
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
      setHighlightedIndex(-1);
    }
  }, [showDropdown, addressResults, highlightedIndex, handleSelectAddressFromDropdown]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown]);

  // Countries list for manual entry
  const countries = useMemo<AddressValue[]>(
    () =>
      COUNTRIES.filter((x) => matchesSearch(x.name, countrySearch))
        .map((x) => ({
          country: x,
          zipCode: '',
          zipPlace: '',
          streetName: '',
          streetNumber: '',
        }))
        .sort((a, b) => (a.country.name > b.country.name ? 1 : -1))
        .sort((x) => (x.country.threeLetterCode === defaultCountry?.threeLetterCode ? -1 : 1)),
    [countrySearch, defaultCountry?.threeLetterCode],
  );

  const getCountryLabel = useCallback((x?: AddressValue) => x?.country?.name ?? '', []);

  const handleOnChangeCountry = useCallback(
    (selected?: AddressValue) => {
      if (selected) {
        setAddress((current) => {
          const updated = { ...current, country: selected.country };
          // Only propagate to the form when an address is already applied; during the
          // search flow the country merely scopes the upcoming lookup.
          if (manualEntry || numberSelected || isEditMode) {
            onChange(updated);
          }
          return updated;
        });
        updateCountrySearch(selected.country.name ?? null);
      }
    },
    [updateCountrySearch, manualEntry, numberSelected, isEditMode, onChange],
  );

  // The toolkit Autocomplete's clear "×" calls onClear (or onChange(null), which the
  // change handler above deliberately ignores). Empty the selection and reset the
  // search filter so the full country list shows for a fresh search.
  const handleClearCountry = useCallback(() => {
    setAddress((current) => {
      const updated = { ...current, country: undefined as unknown as CountryValue };
      // Mirror handleOnChangeCountry: only propagate once an address is applied.
      if (manualEntry || numberSelected || isEditMode) {
        onChange(updated);
      }
      return updated;
    });
    updateCountrySearch(null);
  }, [updateCountrySearch, manualEntry, numberSelected, isEditMode, onChange]);

  // Manual field change handlers
  const handleFieldChange = useCallback((field: keyof AddressValue, value: string) => {
    setAddress((current) => {
      const updated = { ...current, [field]: value };
      // Municipality is a backend-computed "<zip> <place>" projection; derive it here so
      // manually entered addresses pass the address schema (municipality is required).
      updated.municipality = [updated.zipCode, updated.zipPlace].filter(Boolean).join(' ');
      // Also update the form to mark it as dirty
      onChange(updated);
      return updated;
    });
  }, [onChange]);

  const handleToggleManualEntry = useCallback(() => {
    setManualEntry((current) => !current);
    if (!manualEntry) {
      // When switching to manual, set default country
      setAddress((current) => ({
        ...current,
        country: current.country?.threeLetterCode ? current.country : (defaultCountry ?? NORWAY),
      }));
      setStreetSelected(true);
      setNumberSelected(true);
    }
  }, [manualEntry, defaultCountry]);

  const hasCountry = useMemo(
    () => address.country?.threeLetterCode != null && isDefinedString(address.country.threeLetterCode),
    [address.country?.threeLetterCode],
  );

  const hasBuildingAddress = useMemo(() => {
    if (defaultValue == null) return false;
    if (isDefinedString(defaultValue.streetAddress1)) return true;
    if (isDefinedString(defaultValue.municipality)) return true;
    if (!!defaultValue.country?.name && isDefinedString(defaultValue.country.name)) return true;
    return false;
  }, [defaultValue]);

  // Show all fields if: editing existing building, or number has been selected, or manual entry mode
  const showAllFields = isEditMode || numberSelected || manualEntry;

  useEffect(() => {
    updateCountrySearch(defaultValue?.country?.name ?? null);
    setAddress(defaultValue ?? defaultAddress);
    setStreetSelected(!!defaultValue);
    setNumberSelected(!!defaultValue);
  }, [defaultValue]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-4 flex flex-col">
      <div className="flex items-center justify-between">
        <Typography variant="label" className="text-white">
          {ready ? t('buildingCreate.buildingAddress') : <Skeleton variant="text" className="w-[40%]" />}
        </Typography>

        {!editing && (
          <Button color="primary" size="sm" onClick={onToggleEdit} disabled={isSubmitting}>
            {tCommon('edit')}
          </Button>
        )}
      </div>

      {!editing && (
        <>
          {!hasBuildingAddress && (
            <EmptyPlaceholder>{tCommon('noEntity', { entity: tGlobal('building.address') })}</EmptyPlaceholder>
          )}

          {hasBuildingAddress && (
            <div
              className={classNames(
                'flex items-center gap-4 border rounded-md p-4 border-border',
                isSubmitting && 'opacity-70 text-secondary',
              )}
            >
              <FaMapMarkerAlt className="text-red-600" size={16} />

              <div>
                <Typography variant="primary">{defaultValue?.streetAddress1}</Typography>
                <Typography variant="secondary">
                  {combineStrings(', ', defaultValue?.municipality, defaultValue?.country?.name)}
                </Typography>
              </div>
            </div>
          )}
        </>
      )}

      {editing && (
        <>
          {/* Country - scopes the address lookup (Norkart for Norway, Nominatim elsewhere) */}
          <div className="space-y-2">
            <Label className="mb-1">
              {ready ? tGlobal('building.country') : <Skeleton variant="text" className="w-[40%]" />}
            </Label>
            <Autocomplete
              selected={address}
              options={countries}
              getSelectedLabel={getCountryLabel}
              getOptionLabel={getCountryLabel}
              onChange={handleOnChangeCountry}
              onClear={handleClearCountry}
              onInput={updateCountrySearch}
              error={!hasCountry}
            />
            {!hasCountry && (
              <HelperText className="mt-1" error>
                {ready ? t('buildingCreate.countryRequired') : <Skeleton variant="text" className="w-[100%]" />}
              </HelperText>
            )}
          </div>

          {/* Step 1: Street search */}
          <div className="space-y-2">
            <Label className="mb-1">{t('buildingCreate.searchAddress')}</Label>
            <div className="relative">
              <Input
                ref={searchInputRef}
                value={searchQuery}
                onChange={handleSearchInputChange}
                onKeyDown={handleSearchKeyDown}
                onFocus={() => {
                  if (addressResults.length > 0 && !streetSelected) {
                    setShowDropdown(true);
                  }
                }}
                className="w-full pr-10"
                autoComplete="off"
              />
              
              {/* Search icon or loading indicator */}
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                {(isFetchingStreets || streetNameSearchDebouncing) ? (
                  <Spinner size="sm" className="color-brand-primary" />
                ) : (
                  <IoIosSearch className="text-secondary" size="20" />
                )}
              </div>

              {/* Dropdown results */}
              {showDropdown && !streetSelected && (
                <div
                  ref={dropdownRef}
                  className="absolute z-50 w-full mt-1 bg-[#201E1A] rounded shadow-lg max-h-[280px] overflow-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-[#322F2C] [&::-webkit-scrollbar-thumb]:rounded"
                >
                  {addressResults.length > 0 ? (
                    addressResults.map((addr, index) => (
                      <button
                        key={`${addr.streetName}-${addr.streetNumber}-${addr.zipCode}-${index}`}
                        type="button"
                        onClick={() => handleSelectAddressFromDropdown(addr)}
                        onMouseEnter={() => setHighlightedIndex(index)}
                        className={`w-full text-left px-4 py-3 text-sm text-white transition-colors ${
                          index < addressResults.length - 1 ? 'border-b border-[#322F2C]' : ''
                        } ${highlightedIndex === index ? 'bg-[#272521]' : 'hover:bg-[#272521]'}`}
                      >
                        <div className="flex justify-between items-center">
                          <span>{getAddressLabel(addr)}</span>
                          {addr.source?.integration && (
                            <span className="text-xs text-white/60">({addr.source.integration})</span>
                          )}
                        </div>
                      </button>
                    ))
                  ) : searchQuery.length >= 2 && !isFetchingStreets && !streetNameSearchDebouncing ? (
                    <div className="w-full text-left px-4 py-3 text-sm text-white/60">
                      {t('buildingCreate.noResults')}
                    </div>
                  ) : null}
                </div>
              )}
            </div>
            
            {/* Manual entry toggle - free-text override when the lookup has no match */}
            {!streetSelected && !manualEntry && (
              <Button
                color="custom"
                size="sm"
                onClick={handleToggleManualEntry}
                className="bg-surface-elevated hover:bg-surface-elevated/80"
              >
                {t('buildingCreate.enterManually')}
              </Button>
            )}
          </div>

          {/* Detail fields - shown after number selection or in edit mode */}
          {showAllFields && (
            <GridRow cols={{ def: 1, sm: 3 }} spacingX={2} spacingY={4}>
              {/* Adresse (Street Name) and Nummer (Street Number) */}
              <GridCol span={{ def: 1, sm: 2 }}>
                <Label className="mb-1">{tGlobal('building.streetName')}</Label>
                <Input
                  value={address.streetName ?? ''}
                  onChange={(e) => handleFieldChange('streetName', e.target.value)}
                  autoComplete="off"
                />
              </GridCol>

              <GridCol span={{ def: 1, sm: 1 }}>
                <Label className="mb-1">{tGlobal('building.streetNumber')}</Label>
                <Input
                  value={address.streetNumber ?? ''}
                  onChange={(e) => handleFieldChange('streetNumber', e.target.value)}
                  autoComplete="off"
                />
              </GridCol>

              {/* Postnr (Zip Code) and Sted (Zip Place) */}
              <GridCol>
                <Label className="mb-1">{tGlobal('building.zipCode')}</Label>
                <Input
                  value={address.zipCode ?? ''}
                  onChange={(e) => handleFieldChange('zipCode', e.target.value)}
                  autoComplete="off"
                />
              </GridCol>

              <GridCol span={{ def: 1, sm: 2 }}>
                <Label className="mb-1">{tGlobal('building.zipPlace')}</Label>
                <Input
                  value={address.zipPlace ?? ''}
                  onChange={(e) => handleFieldChange('zipPlace', e.target.value)}
                  autoComplete="off"
                />
              </GridCol>

            </GridRow>
          )}

        </>
      )}
    </div>
  );
};
